<template>
  <card-canvas class="workflow-home" size="large" hide-back-button>
    <h2>Workflows</h2>
    <p v-if="$screen.isTabletOrMobile($mq)">
      Workflow management is only available on desktop sized screens.
    </p>
    <template v-else>    
      <p>
        Manage the workflows for all of your projects and mailboxes here.
      </p> 
      <div class="workflow-home__main">
        <loading-placeholder v-if="isLoading" :lines="20" class="loading" />
        <template v-else>
          <div class="workflow-home__main--selector">
            <el-button-group>
              <el-button 
                :class="{'selected':selectedEntityType===EEntities.PROJECTS}"
                @click="updateSelectedEntityType(EEntities.PROJECTS)"
              >Project Workflows</el-button>
              <el-button
                :class="{'selected':selectedEntityType===EEntities.MAILBOXES}"
                @click="updateSelectedEntityType(EEntities.MAILBOXES)"
              >Mailbox Workflows</el-button>
            </el-button-group>
          </div>
          <div class="workflow-home__main--listing">
            <div
              :class="{ 'mailbox' : selectedEntityType===EEntities.MAILBOXES}"
              class="add-new-entity-brick"
              @click="showCreateWorkflowDrawer=true"
            >
              <h3>Add new {{TextAssistant.entitySingularName(selectedEntityType).toLowerCase()}} workflow</h3>
              <quick-add-button :color="ColorAssistant.getEntityColor(selectedEntityType)" size="large" />
            </div>
            <div
              v-for="workflow in filteredWorkflows" 
              :key="workflow.id" 
              :class="[{'mailbox':TypeAssistant.isMailbox(workflow.entityTypeID)}, {'unpublished':!workflow.isPublished}]"
              @click="RouteAssistant.goToWorkflowDetail($router, $route.params.accountId, workflow.id)"
            >
              <div class="title-and-desc">
                <p><strong>{{workflow.name}}</strong></p>
                <p class="small">{{workflow.description}}</p>
              </div>
              <div class="statuses">
                <status-chicklet
                  v-for="status in workflow.listStatuses" 
                  :key="status.id" 
                  :status="status" 
                />
              </div>
              <div v-if="$permissions.jobsFeatureEnabled" class="jobs">
                <chicklet
                  v-for="job in workflow.listJobs" 
                  :key="job.id" 
                  :title="job.name" 
                  is-uppercase
                  size="tiny"
                >
                  <span>{{job.name}}</span>
                </chicklet>
              </div>
              <more-button-dropdown
                v-if="workflow.isPublished || WorkflowsAssistant.isCloneable(workflow.name)"
                class="options" 
                placement="bottom-end" 
                popper-min-width-size="small" 
                @click.stop
              >
                <template #options-list>
                  <ul>
                    <template v-if="workflow.isPublished">
                      <li v-if="TypeAssistant.isMailbox(workflow.entityTypeID)" @click="RouteAssistant.goToCreateMailbox($router, $route.params.accountId, workflow.id)">
                        <i class="ddi ddi-mailbox" />
                        <span>New mailbox&hellip;</span>
                      </li>
                      <li v-else @click="RouteAssistant.goToCreateProject($router, $route.params.accountId, workflow.id)">
                        <i class="ddi ddi-project" />
                        <span>New project&hellip;</span>
                      </li>
                    </template>
                    <li v-if="WorkflowsAssistant.isCloneable(workflow.name)" @click="cloneWorkflow(workflow.id)">
                      <i class="ddi ddi-workflows" />
                      <span>Clone&hellip;</span>
                      <i v-if="isCloning" class="ddi ddi-spinner" />
                    </li>
                  </ul>
                </template>
              </more-button-dropdown>
              <div v-if="!workflow.isEditable" class="ribbon built-in">Built-in</div>
              <div v-if="!workflow.isPublished" class="ribbon draft">Draft</div>
            </div>
          </div>
        </template>
      </div>
      <!-- Create workflow drawer -->
      <create-workflow-drawer 
        v-if="selectedEntityType !== null"
        v-model:show-drawer="showCreateWorkflowDrawer"
        :entity-type="selectedEntityType"
        make-entity-agnostic
        @workflow-created="(id) => RouteAssistant.goToWorkflowDetail($router, $route.params.accountId, id)"
      />
    </template>
  </card-canvas>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */
import CardCanvas from '@/layouts/card-canvas.vue'

/* Helpers */
import LocalStore from '@/helpers/localstore'
import ColorAssistant from '@/helpers/color-assistant'
import TextAssistant from '@/helpers/text-assistant'
import TypeAssistant from '@/helpers/type-assistant'
import RouteAssistant from '@/helpers/route-assistant'
import WorkflowsAssistant from '@/helpers/workflows-assistant'

/* Data types */
import { EEntities } from '@/types'
import { IWorkflowForSelection } from '@/api/types/response.types'

/* API */
import WorkflowsAPI from '@/api/modules/workflows.api'

export default defineComponent({
  name: 'WorkflowHome',

  components: {
    CardCanvas,
  },

  data() {
    return {
      EEntities,
      ColorAssistant,
      TextAssistant,
      TypeAssistant,
      RouteAssistant,
      WorkflowsAssistant,
      isLoading: true,
      isCloning: false,
      workflows: [] as IWorkflowForSelection[],
      showCreateWorkflowDrawer: false,
      selectedEntityType: EEntities.PROJECTS,
    }
  },

  head: {
    title: 'Workflows',
  },

  computed: {
    accountID(): number {
      return parseInt(this.$route.params.accountId, 10)
    },
    filteredWorkflows(): IWorkflowForSelection[] {
      return this.workflows.filter(
        w =>
          this.selectedEntityType === null ||
          w.entityTypeID ===
            TypeAssistant.getEntityTypeID(this.selectedEntityType),
      )
    },
  },

  async created() {
    this.isLoading = true
    this.selectedEntityType = LocalStore.workflowsShowMailboxesFirst
      ? EEntities.MAILBOXES
      : EEntities.PROJECTS

    await this.fetchWorkflows()
    this.isLoading = false
  },

  methods: {
    handleCreateWorkflow() {},
    async fetchWorkflows() {
      try {
        this.workflows = await WorkflowsAPI.get_ForSelection()
      } catch (error) {
        this.$error.emit(error)
      }
    },
    updateSelectedEntityType(entityType: EEntities) {
      this.selectedEntityType = entityType
      LocalStore.workflowsShowMailboxesFirst =
        this.selectedEntityType === EEntities.MAILBOXES
    },
    async cloneWorkflow(id: number) {
      try {
        this.isCloning = true
        const response = await WorkflowsAPI.post_Clone(id)
        RouteAssistant.goToWorkflowDetail(
          this.$router,
          this.$route.params.accountId,
          response.id,
        )
      } catch (err) {
        this.$error.emit(err, true)
      } finally {
        this.isCloning = false
      }
    },
  },
})
</script>

<style lang="scss">
.workflow-home {
  &__main {
    border-top: $border--solid-light;
    margin-top: 30px;
    padding-top: 40px;
    position: relative;

    &--selector {
      position: absolute;
      top: -14px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      justify-content: center;
      @include flex-row-horizontal-spacing(10px);

      .el-button {
        padding: 4px 10px;
        border-radius: 20px;
        background-image: none;
        @include lettering--p-sm;
        font-weight: $bold;
        box-shadow: none;

        &.selected {
          background-color: var(--color-background-secondary);
        }

        &:hover {
          background-image: none;
          background-color: $color--gray-30;
        }
      }
    }

    &--listing {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      grid-gap: 10px;

      > div {
        position: relative;
        border: $border--solid-light;
        border-radius: 8px;
        padding: 24px 20px;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        align-items: center;
        @include standard-box-shadow();
        cursor: pointer;

        &.add-new-entity-brick {
          cursor: pointer;
          background-color: var(--color-background-blue);
          justify-content: center;

          &:hover {
            > * {
              opacity: 1 !important;
            }
          }
        }

        &:hover {
          border-color: $color--blue-60;
          background-color: var(--color-background-blue);
        }

        &.unpublished {
          background-color: var(--color-background-secondary);
        }

        .title-and-desc {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding-bottom: 10px;
          margin-bottom: 10px;
          width: 100%;
          border-bottom: $border--dotted-light;

          > * {
            text-align: center;
            @include multi-line-ellipsis(2);
          }
        }

        .statuses {
          display: flex;
          justify-content: center;
          overflow: hidden;
          @include flex-row-horizontal-spacing(8px);
          @include flex-row-vertical-spacing-on-wrap(8px);
        }

        .jobs {
          padding-top: 20px;
          display: flex;
          justify-content: center;
          overflow: hidden;
          @include flex-row-horizontal-spacing(8px);
          @include flex-row-vertical-spacing-on-wrap(8px);
        }

        .options {
          position: absolute;
          top: 8px;
          right: 6px;

          .ddi-spinner {
            margin-left: auto;
          }
        }

        .ribbon {
          @include lettering--h5--no-color;
          text-align: center;
          display: block;
          color: $color--white;
          padding: 2px 40px;
          transform: rotate(-45deg);
          position: absolute;

          &.built-in {
            background-color: $color--gray-75;
            top: 14px;
            left: -40px;
          }

          &.draft {
            background-color: var(--color-text-third);
            top: 12px;
            left: -38px;
          }
        }

        &.mailbox {
          &.add-new-entity-brick {
            cursor: pointer;
            background-color: var(--color-background-green);
          }

          &:hover {
            border-color: $color--green-60;
            background-color: var(--color-background-green);
          }
        }
      }
    }
  }
}
</style>
